/**
 * IncidentReports — admin list of incident & accident reports.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Filter } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

const STATUS_PALETTE = {
  draft: "bg-[#EEF0F4] text-[#6B7280]",
  pending_ack: "bg-[#FDF1D2] text-[#8C6D1F]",
  acknowledged: "bg-[#E6EFE8] text-[#1F3A2A]",
  closed: "bg-[#EEF0F4] text-[#9CA3AF]",
};
const SEVERITY_PALETTE = {
  low: "text-[#6B7280]",
  medium: "text-[#8C6D1F]",
  high: "text-[#A11D1D]",
  critical: "text-[#A11D1D] font-semibold",
};

export default function IncidentReports() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const url = status
        ? `/admin/ops/incidents?status=${status}`
        : "/admin/ops/incidents";
      const r = await api.get(url);
      setItems(r.data || []);
    } catch {
      // ignore
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <main className="p-6 lg:p-10 space-y-6" data-testid="admin-incidents">
      <AdminHeader
        eyebrow={t("ops.incidents.eyebrow")}
        title={t("ops.incidents.title")}
        action={
          <Link
            to="/admin/ops/incidents/new"
            data-testid="incident-create-link"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            <Plus size={14} strokeWidth={2} />
            {t("ops.incidents.create_cta")}
          </Link>
        }
      />

      <div className="flex items-center gap-2">
        <Filter size={14} strokeWidth={1.6} className="text-[#6B7280]" />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="incident-status-filter"
          className="text-sm bg-white border border-[#EEF0F4] rounded-full px-3 py-2 focus:outline-none focus:border-[#1F3A2A]"
        >
          <option value="">{t("ops.incidents.filter_status_all")}</option>
          <option value="draft">{t("ops.incidents.filter_status_draft")}</option>
          <option value="pending_ack">{t("ops.incidents.filter_status_pending_ack")}</option>
          <option value="acknowledged">{t("ops.incidents.filter_status_acknowledged")}</option>
          <option value="closed">{t("ops.incidents.filter_status_closed")}</option>
        </select>
      </div>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[#6B7280] uppercase tracking-[0.1em] text-[10px] bg-[#FAFAFB]">
              <tr>
                <th className="px-5 py-3">{t("ops.incidents.table_number")}</th>
                <th className="px-5 py-3">{t("ops.incidents.table_child")}</th>
                <th className="px-5 py-3">{t("ops.incidents.table_type")}</th>
                <th className="px-5 py-3">{t("ops.incidents.table_severity")}</th>
                <th className="px-5 py-3">{t("ops.incidents.table_occurred")}</th>
                <th className="px-5 py-3">{t("ops.incidents.table_status")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center font-serif italic text-[#9CA3AF]">
                    {t("ops.common.loading")}
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center font-serif italic text-[#9CA3AF]" data-testid="incidents-empty">
                    No incidents yet — calm is the default state.
                  </td>
                </tr>
              ) : (
                items.map((inc) => (
                  <tr
                    key={inc.id}
                    data-testid={`incident-row-${inc.id}`}
                    className="border-t border-[#EEF0F4] hover:bg-[#FAFAFB] transition-colors"
                  >
                    <td className="px-5 py-3">
                      <Link to={`/admin/ops/incidents/${inc.id}`} className="font-medium text-[#1A1B2E] hover:text-[#1F3A2A]">
                        {inc.incident_number}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-[#1A1B2E]">{inc.child_name || "—"}</td>
                    <td className="px-5 py-3 text-[#6B7280]">
                      {t(`ops.incidents.type_${inc.incident_type}`)}
                    </td>
                    <td className={`px-5 py-3 ${SEVERITY_PALETTE[inc.severity]}`}>
                      {t(`ops.incidents.severity_${inc.severity}`)}
                    </td>
                    <td className="px-5 py-3 text-[#6B7280] tabular-nums">
                      {inc.occurred_at?.slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] tracking-[0.1em] uppercase font-semibold rounded-full px-2 py-0.5 ${STATUS_PALETTE[inc.status]}`}>
                        {t(`ops.incidents.status_${inc.status}`)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
